import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Form,
  InputNumber,
  Modal,
  message,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import { Package, RefreshCw } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { marketApi } from '@/services/marketApi';
import { brandStore } from '@/stores';
import type {
  MarketApplicationTarget,
  MarketAuditLog,
  MarketCategory,
  MarketItem,
  MarketListing,
  MarketRelease,
  MarketType,
  ProtoInt64,
} from '@/types';
import { isNextopBrand } from '@/utils/brand';
import { SkillUploadDialog } from '../SkillManagement/SkillUploadDialog';
import {
  marketItemDisplay,
  skillListingCandidates,
  skillManifestSummary,
  skillNeedsVersionSelection,
} from '../SkillManagement/skillPresentation';
import { CategoryManager } from './CategoryManager';
import { findNewerAvailableRelease } from './versionAvailability';

const marketLabels: Record<MarketType, string> = {
  domestic: '国内',
  overseas: '海外',
};

const categoryLabels: Record<string, string> = {
  productivity: '生产力',
  communication: '沟通协作',
  'developer-tools': '开发者工具',
  creativity: '创意设计',
  'business-operations': '商业与运营',
  development: '开发者工具',
  other: '其他',
};

interface ListingFormValues {
  isListed: boolean;
  categoryId: string;
  categoryRank: number;
  featured: boolean;
  featuredRank: number;
}

interface ListingTarget {
  item: MarketItem;
  marketType: MarketType;
}

function categoryLabel(categoryId?: string) {
  return categoryLabels[categoryId ?? ''] ?? categoryId ?? '其他';
}

function parseObject(value?: string): Record<string, unknown> {
  try {
    const parsed: unknown = JSON.parse(value ?? '');
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function nestedObject(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : undefined;
}

function formatTime(value?: ProtoInt64) {
  const timestamp = Number(value);
  return Number.isFinite(timestamp) && timestamp > 0
    ? new Date(timestamp).toLocaleString('zh-CN')
    : '—';
}

function formatBytes(value: ProtoInt64) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) {
    return '—';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KiB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(1)} MiB`;
}

function errorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string; reason?: string }
      | undefined;
    return data?.message || data?.reason || error.message || fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

function listingErrorMessage(
  error: unknown,
  marketType: MarketType,
  itemType: 'connector' | 'skill',
) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string; reason?: string }
      | undefined;
    if (data?.reason === 'MARKET_PRECONDITION_FAILED') {
      const detail = data.message?.toLowerCase() ?? '';
      if (
        itemType === 'connector' &&
        detail.includes('does not support market')
      ) {
        return `无法上架${marketLabels[marketType]}市场：当前应用服务仍在按旧版连接器市场声明校验，请升级后重试`;
      }
      if (detail.includes('select an active release')) {
        return `无法上架${marketLabels[marketType]}市场：请先通过“切换版本”选择当前版本`;
      }
      if (detail.includes('not available') || detail.includes('unavailable')) {
        return `无法上架${marketLabels[marketType]}市场：当前版本不可用，请先切换到状态为“可用”的版本`;
      }
      return `无法上架${marketLabels[marketType]}市场：当前版本元数据不符合发布要求，请检查版本详情`;
    }
    if (data?.reason === 'MARKET_CONFLICT') {
      return '市场配置已被其他管理员更新，请刷新后再试';
    }
  }
  return errorMessage(error, '市场配置更新失败，请刷新后重试');
}

function listingOf(item: MarketItem, marketType: MarketType) {
  return item.listings.find(
    (listing: MarketListing) => listing.marketType === marketType,
  );
}

function itemIdentity(item: MarketItem) {
  return `${item.itemType}:${item.itemKey}`;
}

function MarketManifestOverview({ item }: { item: MarketItem }) {
  const manifest = parseObject(item.activeRelease?.manifestJson);
  const payload = nestedObject(manifest.payload) ?? {};
  const implementation = nestedObject(payload.implementation);
  const legacyImplementations = nestedObject(payload.implementations);
  const implementationKind =
    (typeof implementation?.kind === 'string' && implementation.kind) ||
    Object.values(legacyImplementations ?? {})
      .map((value) => nestedObject(value)?.kind)
      .find((value): value is string => typeof value === 'string') ||
    '—';
  if (!item.activeRelease) {
    return <Empty description="尚未选择当前版本" />;
  }
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Descriptions bordered size="small" column={1}>
        {item.itemType === 'skill' ? (
          <>
            <Descriptions.Item label="入口">
              {skillManifestSummary(item).entrypoint}
            </Descriptions.Item>
            <Descriptions.Item label="支持的 Agent">
              {skillManifestSummary(item).agentProviders.join(', ') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="最低 Computer 版本">
              {skillManifestSummary(item).minimumHostVersion}
            </Descriptions.Item>
          </>
        ) : (
          <Descriptions.Item label="运行实现">
            {implementationKind}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="上架范围">
          由下方国内、海外市场配置决定
        </Descriptions.Item>
      </Descriptions>
      <pre className="max-h-[440px] overflow-auto rounded-lg bg-gray-950 p-4 text-xs text-gray-100">
        {JSON.stringify(manifest, null, 2)}
      </pre>
    </Space>
  );
}

interface ManagementProps {
  itemType: 'connector' | 'skill';
}

export const MarketManagementPage = observer((props: ManagementProps) => {
  const { itemType } = props;
  const itemLabel = itemType === 'skill' ? 'Skill' : '连接器';
  const pageTitle = itemType === 'skill' ? 'Skill 市场' : '连接器管理';
  const [listingForm] = Form.useForm<ListingFormValues>();
  const activeBrand = brandStore.activeBrand;
  const available = isNextopBrand(activeBrand);
  const marketTarget: MarketApplicationTarget = 'tutti-server';
  const [items, setItems] = useState<MarketItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionKey, setActionKey] = useState('');
  const [detail, setDetail] = useState<MarketItem>();
  const [detailReleases, setDetailReleases] = useState<MarketRelease[]>([]);
  const [auditLogs, setAuditLogs] = useState<MarketAuditLog[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const itemsRequestSequence = useRef(0);
  const detailRequestSequence = useRef(0);
  const [versionTarget, setVersionTarget] = useState<MarketItem>();
  const [versionReleases, setVersionReleases] = useState<MarketRelease[]>([]);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [versionLoading, setVersionLoading] = useState(false);
  const versionRequestSequence = useRef(0);
  const [listingTarget, setListingTarget] = useState<ListingTarget>();
  const [listingCategories, setListingCategories] = useState<MarketCategory[]>(
    [],
  );
  const [listingCategoriesLoading, setListingCategoriesLoading] =
    useState(false);
  const [listingCategoriesError, setListingCategoriesError] = useState('');
  const listingRequestSequence = useRef(0);
  const featuredEnabled = Form.useWatch('featured', listingForm);
  const lifetime = useRef<AbortController | null>(null);
  const confirmDialog = useRef<ReturnType<typeof Modal.confirm> | null>(null);
  const listingAfterVersion = useRef<MarketType | undefined>(undefined);
  const [addListingOpen, setAddListingOpen] = useState(false);
  const [addListingKey, setAddListingKey] = useState('');
  const [addListingMarket, setAddListingMarket] =
    useState<MarketType>('overseas');
  const listingCandidates = useMemo(
    () => skillListingCandidates(items),
    [items],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: A change of feature or Brand availability retires every request from the previous view.
  useEffect(() => {
    const controller = new AbortController();
    lifetime.current = controller;
    setItems([]);
    setDetail(undefined);
    setVersionTarget(undefined);
    setListingTarget(undefined);
    setAddListingOpen(false);
    setActionKey('');
    return () => {
      controller.abort();
      itemsRequestSequence.current += 1;
      detailRequestSequence.current += 1;
      versionRequestSequence.current += 1;
      listingRequestSequence.current += 1;
      listingAfterVersion.current = undefined;
      confirmDialog.current?.destroy();
      confirmDialog.current = null;
    };
  }, [available, itemType]);

  const replaceItem = useCallback((next: MarketItem) => {
    setItems((current) =>
      current.map((item) =>
        itemIdentity(item) === itemIdentity(next)
          ? {
              ...next,
              latestAvailableRelease:
                next.latestAvailableRelease ?? item.latestAvailableRelease,
            }
          : item,
      ),
    );
    setDetail((current) =>
      current && itemIdentity(current) === itemIdentity(next)
        ? {
            ...next,
            latestAvailableRelease:
              next.latestAvailableRelease ?? current.latestAvailableRelease,
          }
        : current,
    );
  }, []);

  const loadItems = useCallback(async () => {
    const requestSequence = itemsRequestSequence.current + 1;
    itemsRequestSequence.current = requestSequence;
    if (!available) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const response = await marketApi.listItems(marketTarget, itemType, {
        signal: lifetime.current?.signal,
      });
      if (itemsRequestSequence.current === requestSequence) {
        setItems(
          (response.items ?? []).filter((item) => item.itemType === itemType),
        );
      }
    } catch (error) {
      if (itemsRequestSequence.current === requestSequence) {
        message.error(errorMessage(error, `${itemLabel}加载失败`));
      }
    } finally {
      if (itemsRequestSequence.current === requestSequence) {
        setLoading(false);
      }
    }
  }, [available, itemType, itemLabel]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  const loadDetail = useCallback(
    async (item: MarketItem) => {
      const requestSequence = detailRequestSequence.current + 1;
      detailRequestSequence.current = requestSequence;
      setDetail(item);
      setDetailReleases([]);
      setAuditLogs([]);
      setDetailError('');
      setDetailLoading(true);
      try {
        const [releases, audit] = await Promise.all([
          marketApi.listReleases(marketTarget, item.itemType, item.itemKey, {
            signal: lifetime.current?.signal,
          }),
          marketApi.listAuditLogs(
            marketTarget,
            item.itemType,
            item.itemKey,
            100,
            { signal: lifetime.current?.signal },
          ),
        ]);
        if (detailRequestSequence.current !== requestSequence) {
          return;
        }
        setDetailReleases(releases.releases ?? []);
        setAuditLogs(audit.auditLogs ?? []);
      } catch (error) {
        if (detailRequestSequence.current !== requestSequence) {
          return;
        }
        const detailLoadError = errorMessage(error, `${itemLabel}详情加载失败`);
        setDetailError(detailLoadError);
        message.error(detailLoadError);
      } finally {
        if (detailRequestSequence.current === requestSequence) {
          setDetailLoading(false);
        }
      }
    },
    [itemLabel],
  );

  const closeDetail = useCallback(() => {
    detailRequestSequence.current += 1;
    setDetail(undefined);
    setDetailReleases([]);
    setAuditLogs([]);
    setDetailError('');
    setDetailLoading(false);
  }, []);

  const openVersionDialog = useCallback(
    async (
      item: MarketItem,
      preferredVersion?: string,
      thenListIn?: MarketType,
    ) => {
      listingAfterVersion.current = thenListIn;
      const requestSequence = versionRequestSequence.current + 1;
      versionRequestSequence.current = requestSequence;
      setVersionTarget(item);
      setSelectedVersion(preferredVersion ?? item.activeVersion);
      setVersionLoading(true);
      try {
        const response = await marketApi.listReleases(
          marketTarget,
          item.itemType,
          item.itemKey,
          { signal: lifetime.current?.signal },
        );
        if (versionRequestSequence.current === requestSequence) {
          setVersionReleases(response.releases ?? []);
        }
      } catch (error) {
        if (versionRequestSequence.current === requestSequence) {
          message.error(errorMessage(error, '版本列表加载失败'));
          setVersionTarget(undefined);
        }
      } finally {
        if (versionRequestSequence.current === requestSequence) {
          setVersionLoading(false);
        }
      }
    },
    [],
  );

  const closeVersionDialog = useCallback(() => {
    listingAfterVersion.current = undefined;
    versionRequestSequence.current += 1;
    setVersionTarget(undefined);
    setVersionReleases([]);
    setSelectedVersion('');
    setVersionLoading(false);
  }, []);

  const confirmVersion = async () => {
    if (!versionTarget || !selectedVersion) {
      return;
    }
    const signal = lifetime.current?.signal;
    if (!signal || signal.aborted) return;
    const requestSequence = versionRequestSequence.current;
    const thenListIn = listingAfterVersion.current;
    const detailSequence = detailRequestSequence.current;
    setVersionLoading(true);
    try {
      const response = await marketApi.setActiveRelease(
        marketTarget,
        {
          itemType: versionTarget.itemType,
          itemKey: versionTarget.itemKey,
          version: selectedVersion,
          expectedLockVersion: versionTarget.lockVersion,
        },
        { signal },
      );
      if (signal.aborted) return;
      if (versionRequestSequence.current !== requestSequence) {
        // The write may have committed after its dialog was closed. Reconcile
        // the list without reviving or modifying another dialog.
        void loadItems();
        return;
      }
      replaceItem(response.item);
      setVersionTarget(undefined);
      message.success('统一版本已更新，国内和海外市场同步生效');
      listingAfterVersion.current = undefined;
      if (thenListIn) openListingDialog(response.item, thenListIn, true);
      if (
        detailRequestSequence.current === detailSequence &&
        detail &&
        itemIdentity(detail) === itemIdentity(response.item)
      ) {
        await loadDetail(response.item);
      }
    } catch (error) {
      if (signal.aborted || versionRequestSequence.current !== requestSequence)
        return;
      message.error(errorMessage(error, '版本切换失败，请刷新后重试'));
      await loadItems();
    } finally {
      if (!signal.aborted && versionRequestSequence.current === requestSequence)
        setVersionLoading(false);
    }
  };

  const loadListingCategories = useCallback(
    async (item: MarketItem, marketType: MarketType) => {
      const requestSequence = listingRequestSequence.current + 1;
      listingRequestSequence.current = requestSequence;
      setListingCategories([]);
      setListingCategoriesError('');
      setListingCategoriesLoading(true);
      try {
        const response = await marketApi.listCategories(
          marketTarget,
          {
            itemType: item.itemType,
            marketType,
          },
          { signal: lifetime.current?.signal },
        );
        if (listingRequestSequence.current !== requestSequence) {
          return;
        }
        const categories = (response.categories ?? [])
          .filter((category) => category.kind === 'category')
          .sort((left, right) => left.sortOrder - right.sortOrder);
        if (categories.length === 0) {
          setListingCategoriesError('服务端没有返回可用分类');
          return;
        }
        setListingCategories(categories);
      } catch (error) {
        if (listingRequestSequence.current !== requestSequence) {
          return;
        }
        setListingCategoriesError(errorMessage(error, '分类加载失败'));
      } finally {
        if (listingRequestSequence.current === requestSequence) {
          setListingCategoriesLoading(false);
        }
      }
    },
    [],
  );

  const openListingDialog = useCallback(
    (item: MarketItem, marketType: MarketType, preferListed = false) => {
      const listing = listingOf(item, marketType);
      setListingTarget({ item, marketType });
      listingForm.setFieldsValue({
        isListed: preferListed || (listing?.isListed ?? false),
        categoryId: listing?.categoryId || 'other',
        categoryRank: listing?.categoryRank ?? 0,
        featured: listing?.featured ?? false,
        featuredRank: listing?.featuredRank ?? 0,
      });
      void loadListingCategories(item, marketType);
    },
    [listingForm, loadListingCategories],
  );

  const closeListingDialog = useCallback(() => {
    listingRequestSequence.current += 1;
    setListingTarget(undefined);
    setListingCategories([]);
    setListingCategoriesError('');
    setListingCategoriesLoading(false);
    listingForm.resetFields();
  }, [listingForm]);

  const confirmListing = useCallback(async () => {
    if (!listingTarget) {
      return;
    }
    let values: ListingFormValues;
    try {
      values = await listingForm.validateFields();
    } catch {
      return;
    }
    const signal = lifetime.current?.signal;
    if (!signal || signal.aborted) return;
    const currentListing = listingOf(
      listingTarget.item,
      listingTarget.marketType,
    );
    const key = `${itemIdentity(listingTarget.item)}:${listingTarget.marketType}`;
    setActionKey(key);
    try {
      const response = await marketApi.setListing(
        marketTarget,
        {
          itemType: listingTarget.item.itemType,
          itemKey: listingTarget.item.itemKey,
          marketType: listingTarget.marketType,
          isListed: values.isListed,
          expectedIsListed: currentListing?.isListed ?? false,
          categoryId: values.categoryId,
          categoryRank: values.categoryRank,
          featured: values.featured,
          featuredRank: values.featured ? values.featuredRank : 0,
        },
        { signal },
      );
      if (signal.aborted) return;
      replaceItem(response.item);
      message.success(
        `${marketLabels[listingTarget.marketType]}市场配置已更新`,
      );
      closeListingDialog();
    } catch (error) {
      if (signal.aborted) return;
      message.error(
        listingErrorMessage(error, listingTarget.marketType, itemType),
      );
      await loadItems();
      closeListingDialog();
    } finally {
      if (!signal.aborted) setActionKey('');
    }
  }, [
    closeListingDialog,
    listingForm,
    listingTarget,
    loadItems,
    replaceItem,
    itemType,
  ]);

  const revokeRelease = useCallback(
    (release: MarketRelease) => {
      if (
        !detail ||
        itemIdentity(detail) !== `${release.itemType}:${release.itemKey}`
      ) {
        message.error('详情数据已失效，请重新打开后再操作');
        return;
      }
      const signal = lifetime.current?.signal;
      if (!signal || signal.aborted) return;
      confirmDialog.current = Modal.confirm({
        title: `撤销 ${release.version}`,
        content: '撤销后该版本不能再设为当前版本，此操作会写入审计日志',
        okText: '确认撤销',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: async () => {
          if (signal.aborted) return;
          const key = `${release.itemType}:${release.itemKey}:${release.version}:revoke`;
          setActionKey(key);
          try {
            await marketApi.revokeRelease(
              marketTarget,
              {
                itemType: release.itemType,
                itemKey: release.itemKey,
                version: release.version,
              },
              { signal },
            );
            if (signal.aborted) return;
            message.success('版本已撤销');
            if (detail) {
              await loadDetail(detail);
            }
            await loadItems();
          } catch (error) {
            if (signal.aborted) return;
            message.error(errorMessage(error, '版本撤销失败'));
            throw error;
          } finally {
            if (!signal.aborted) setActionKey('');
          }
        },
      });
    },
    [detail, loadDetail, loadItems],
  );

  const deleteItem = useCallback(
    (item: MarketItem) => {
      const signal = lifetime.current?.signal;
      if (!signal || signal.aborted) return;
      confirmDialog.current = Modal.confirm({
        title: `删除${itemLabel} ${item.itemKey}`,
        content: (
          <Space direction="vertical" size={8}>
            <Typography.Text>
              删除市场条目后无法恢复。若只需停止新用户添加，请使用下架。
            </Typography.Text>
            <Typography.Text type="danger">
              {itemLabel} 的全部版本、国内外上架配置和审计记录都会被永久删除。
            </Typography.Text>
            {itemType === 'skill' ? (
              <Typography.Text type="danger">
                账号已保存的 Skill
                会标记为不可用；已准备的会话文件不会在这里删除。
              </Typography.Text>
            ) : null}
            <Typography.Text type="secondary">
              已上传的制品对象不由 Market 管理，不会随元数据一起删除。
            </Typography.Text>
          </Space>
        ),
        okText: '确认删除',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: async () => {
          if (signal.aborted) return;
          const identity = itemIdentity(item);
          const key = `${identity}:delete`;
          setActionKey(key);
          try {
            await marketApi.deleteItem(
              marketTarget,
              {
                itemType: item.itemType,
                itemKey: item.itemKey,
                expectedLockVersion: item.lockVersion,
              },
              { signal },
            );
            if (signal.aborted) return;
            if (detail && itemIdentity(detail) === identity) {
              closeDetail();
            }
            setItems((current) =>
              current.filter(
                (candidate) => itemIdentity(candidate) !== identity,
              ),
            );
            message.success(`${itemLabel} ${item.itemKey} 已删除`);
          } catch (error) {
            if (signal.aborted) return;
            message.error(
              errorMessage(error, `${itemLabel}删除失败，请刷新后重试`),
            );
            await loadItems();
            throw error;
          } finally {
            if (!signal.aborted) setActionKey('');
          }
        },
      });
    },
    [closeDetail, detail, loadItems, itemLabel, itemType],
  );

  const columns: ColumnsType<MarketItem> = useMemo(
    () => [
      {
        title: itemLabel,
        key: 'item',
        render: (_, item) => {
          const display = marketItemDisplay(item);
          return (
            <Space align="start">
              {display.iconUrl ? (
                <span className="flex h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-border">
                  <img
                    alt=""
                    className="h-full w-full object-cover"
                    height={40}
                    src={display.iconUrl}
                    width={40}
                  />
                </span>
              ) : (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100">
                  <Package className="h-5 w-5 text-muted-foreground" />
                </span>
              )}
              <div>
                <Space>
                  <span className="font-medium">{display.name}</span>
                </Space>
                <div className="font-mono text-xs text-muted-foreground">
                  {item.itemKey}
                </div>
                {display.description ? (
                  <div className="mt-1 max-w-[320px] truncate text-xs text-muted-foreground">
                    {display.description}
                  </div>
                ) : null}
              </div>
            </Space>
          );
        },
      },
      {
        title: '统一版本',
        key: 'version',
        width: 180,
        render: (_, item) => {
          const newerRelease = findNewerAvailableRelease(item);
          return (
            <Space direction="vertical" size={2}>
              {item.activeVersion ? (
                <Tag color="blue">{item.activeVersion}</Tag>
              ) : (
                <Tag>未选择</Tag>
              )}
              {newerRelease ? (
                <Tag color="orange">新版本 {newerRelease.version} 可用</Tag>
              ) : null}
              <Button
                size="small"
                type="link"
                onClick={() =>
                  void openVersionDialog(item, newerRelease?.version)
                }
              >
                切换版本
              </Button>
            </Space>
          );
        },
      },
      ...(['domestic', 'overseas'] as MarketType[]).map((marketType) => ({
        title: `${marketLabels[marketType]}市场`,
        key: marketType,
        width: 190,
        render: (_: unknown, item: MarketItem) => {
          const listing = listingOf(item, marketType);
          const key = `${itemIdentity(item)}:${marketType}`;
          return (
            <Space direction="vertical" size={4}>
              <Space size={4} wrap>
                <Tag color={listing?.isListed ? 'green' : undefined}>
                  {listing?.isListed ? '已上架' : '已下架'}
                </Tag>
                <Tag>{categoryLabel(listing?.categoryId)}</Tag>
                {listing?.featured ? <Tag color="gold">精选</Tag> : null}
              </Space>
              <Button
                size="small"
                type="link"
                loading={actionKey === key}
                onClick={() => {
                  if (
                    itemType === 'skill' &&
                    skillNeedsVersionSelection(item)
                  ) {
                    void openVersionDialog(
                      item,
                      item.latestAvailableRelease?.version,
                      marketType,
                    );
                  } else
                    openListingDialog(
                      item,
                      marketType,
                      itemType === 'skill' && !listing?.isListed,
                    );
                }}
              >
                {itemType === 'skill' && !listing?.isListed ? '上架' : '配置'}
              </Button>
            </Space>
          );
        },
      })),
      {
        title: '来源',
        key: 'source',
        render: (_, item) => (
          <div className="max-w-[240px] font-mono text-xs">
            <div>{item.sourceRepository}</div>
            <div className="truncate text-muted-foreground">
              {item.sourcePath || '/'}
            </div>
          </div>
        ),
      },
      {
        title: '操作',
        key: 'actions',
        width: 140,
        render: (_, item) => (
          <Space size={0}>
            <Button type="link" onClick={() => void loadDetail(item)}>
              详情
            </Button>
            <Button
              danger
              type="link"
              loading={actionKey === `${itemIdentity(item)}:delete`}
              onClick={() => deleteItem(item)}
            >
              删除
            </Button>
          </Space>
        ),
      },
    ],
    [
      actionKey,
      deleteItem,
      loadDetail,
      openListingDialog,
      openVersionDialog,
      itemLabel,
      itemType,
    ],
  );

  const releaseColumns: ColumnsType<MarketRelease> = [
    {
      title: '版本',
      dataIndex: 'version',
      render: (version: string, release) => (
        <Space>
          <span className="font-mono">{version}</span>
          {detail?.activeVersion === version ? (
            <Tag color="blue">当前</Tag>
          ) : null}
          <Tag color={release.status === 'available' ? 'green' : 'red'}>
            {release.status === 'available' ? '可用' : '已撤销'}
          </Tag>
        </Space>
      ),
    },
    {
      title: '制品',
      key: 'artifact',
      render: (_, release) => (
        <Tooltip title={`${release.artifactKey}\n${release.artifactSha256}`}>
          <span className="font-mono text-xs">
            {formatBytes(release.artifactSizeBytes)} ·{' '}
            {release.artifactSha256.slice(0, 10)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '发布时间',
      dataIndex: 'publishedAtMs',
      render: formatTime,
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, release) => (
        <Space>
          <Button
            size="small"
            disabled={
              !detail ||
              itemIdentity(detail) !==
                `${release.itemType}:${release.itemKey}` ||
              release.status !== 'available' ||
              detail?.activeVersion === release.version
            }
            onClick={() =>
              detail && void openVersionDialog(detail, release.version)
            }
          >
            设为当前
          </Button>
          <Button
            danger
            size="small"
            disabled={
              !detail ||
              itemIdentity(detail) !==
                `${release.itemType}:${release.itemKey}` ||
              release.status !== 'available' ||
              detail?.activeVersion === release.version
            }
            loading={
              actionKey ===
              `${release.itemType}:${release.itemKey}:${release.version}:revoke`
            }
            onClick={() => revokeRelease(release)}
          >
            撤销
          </Button>
        </Space>
      ),
    },
  ];

  const auditColumns: ColumnsType<MarketAuditLog> = [
    { title: '事件', dataIndex: 'eventType', width: 170 },
    {
      title: '市场',
      dataIndex: 'marketType',
      width: 90,
      render: (value: string) =>
        marketLabels[value as MarketType] ?? (value || '—'),
    },
    {
      title: '操作者',
      key: 'actor',
      width: 180,
      render: (_, entry) => `${entry.actorType}:${entry.actorId || '—'}`,
    },
    {
      title: '变更',
      dataIndex: 'changeJson',
      render: (value: string) => (
        <Tooltip title={value}>
          <span className="block max-w-[360px] truncate font-mono text-xs">
            {value || '—'}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '时间',
      dataIndex: 'createdAtMs',
      width: 180,
      render: formatTime,
    },
  ];

  if (!available) {
    return (
      <Card>
        <Typography.Title level={3} style={{ marginTop: 0 }}>
          {pageTitle}
        </Typography.Title>
        <Empty
          description={`${pageTitle}仅在 Tutti Brand 下可用，请先切换 Brand。`}
        />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {pageTitle}
          </Typography.Title>
          <Typography.Text type="secondary">
            管理{itemLabel}版本、国内和海外上架状态及分类陈列
          </Typography.Text>
        </div>
        <Space wrap>
          {itemType === 'skill' ? (
            <SkillUploadDialog onPublished={() => void loadItems()} />
          ) : null}
          {itemType === 'skill' ? (
            <Button
              disabled={loading || listingCandidates.length === 0}
              onClick={() => {
                setAddListingKey('');
                setAddListingOpen(true);
              }}
            >
              上架已有 Skill
            </Button>
          ) : null}
          <CategoryManager
            key={`${marketTarget}:${itemType}`}
            target={marketTarget}
            fixedItemType={itemType === 'skill' ? 'skill' : undefined}
          />
          <Button
            icon={<RefreshCw className="h-4 w-4" />}
            loading={loading}
            onClick={() => void loadItems()}
          >
            刷新
          </Button>
        </Space>
      </div>

      <Alert
        showIcon
        type="info"
        message={`${itemLabel}发布与上架`}
        description={
          itemType === 'skill'
            ? '上传 ZIP 发布 Skill，再选择当前版本与上架市场。国内、海外分别配置；账号已添加的版本保持不变。'
            : 'CI 发布不可变连接器版本；国内和海外独立管理上架与分类陈列，共享当前版本。'
        }
      />

      <Card>
        <Table
          columns={columns}
          dataSource={items}
          loading={loading}
          locale={{
            emptyText: <Empty description={`尚未发布${itemLabel}`} />,
          }}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          rowKey={itemIdentity}
          scroll={{ x: 1080 }}
        />
      </Card>

      <Modal
        title="上架已有 Skill"
        open={addListingOpen}
        okText="继续配置"
        cancelText="取消"
        okButtonProps={{ disabled: !addListingKey }}
        onCancel={() => setAddListingOpen(false)}
        onOk={() => {
          const item = listingCandidates.find(
            (candidate) => candidate.itemKey === addListingKey,
          );
          if (!item) return;
          setAddListingOpen(false);
          if (skillNeedsVersionSelection(item))
            void openVersionDialog(
              item,
              item.latestAvailableRelease?.version,
              addListingMarket,
            );
          else openListingDialog(item, addListingMarket, true);
        }}
      >
        <Alert
          className="mb-4"
          showIcon
          type="info"
          message="选择已发布的 Skill"
          description="这里只管理已有发布版本的上架。当前版本不可用或尚未选择版本的 Skill，会先进入版本选择。"
        />
        <Space direction="vertical" className="w-full">
          <Typography.Text>Skill</Typography.Text>
          <Select
            aria-label="选择已有 Skill"
            className="w-full"
            placeholder="选择待上架 Skill"
            value={addListingKey || undefined}
            onChange={setAddListingKey}
            options={listingCandidates.map((item) => ({
              value: item.itemKey,
              label: `${marketItemDisplay(item).name} · ${item.itemKey}`,
            }))}
          />
          <Typography.Text>市场</Typography.Text>
          <Select
            aria-label="选择上架市场"
            className="w-full"
            value={addListingMarket}
            onChange={setAddListingMarket}
            options={[
              { value: 'domestic', label: '国内市场' },
              { value: 'overseas', label: '海外市场' },
            ]}
          />
        </Space>
      </Modal>

      <Modal
        title={
          versionTarget
            ? `切换${itemLabel} / ${versionTarget.itemKey} 的统一版本`
            : ''
        }
        open={Boolean(versionTarget)}
        okText="确认切换"
        cancelText="取消"
        confirmLoading={versionLoading}
        okButtonProps={{ disabled: !selectedVersion }}
        onCancel={closeVersionDialog}
        onOk={() => void confirmVersion()}
      >
        <Alert
          className="mb-4"
          showIcon
          type="warning"
          message="已上架的国内和海外市场会同时使用新版本"
        />
        <Select
          className="w-full"
          loading={versionLoading}
          placeholder="选择可用版本"
          value={selectedVersion || undefined}
          options={versionReleases
            .filter((release) => release.status === 'available')
            .map((release) => ({
              value: release.version,
              label: `${release.version} · ${formatTime(release.publishedAtMs)}`,
            }))}
          onChange={setSelectedVersion}
        />
      </Modal>

      <Modal
        destroyOnHidden
        title={
          listingTarget
            ? `${marketLabels[listingTarget.marketType]}市场配置 / ${listingTarget.item.itemKey}`
            : ''
        }
        open={Boolean(listingTarget)}
        okText="保存配置"
        cancelText="取消"
        confirmLoading={
          listingTarget
            ? actionKey ===
              `${itemIdentity(listingTarget.item)}:${listingTarget.marketType}`
            : false
        }
        okButtonProps={{
          disabled:
            listingCategoriesLoading ||
            Boolean(listingCategoriesError) ||
            listingCategories.length === 0,
        }}
        onCancel={closeListingDialog}
        onOk={() => void confirmListing()}
      >
        <Alert
          className="mb-4"
          showIcon
          type="info"
          message="上架范围由管理端配置"
          description={
            itemType === 'skill'
              ? '开启上架后，当前版本会出现在对应市场；两个市场可以分别配置。下架不会撤销已有账号保存的版本。'
              : '连接器版本不再声明国内或海外范围。开启上架后，当前版本会出现在对应市场；两个市场可以分别配置。'
          }
        />
        {listingCategoriesError ? (
          <Alert
            className="mb-4"
            showIcon
            type="error"
            message="服务端分类加载失败"
            description={listingCategoriesError}
            action={
              listingTarget ? (
                <Button
                  size="small"
                  onClick={() =>
                    void loadListingCategories(
                      listingTarget.item,
                      listingTarget.marketType,
                    )
                  }
                >
                  重试
                </Button>
              ) : null
            }
          />
        ) : null}
        <Form<ListingFormValues>
          form={listingForm}
          layout="vertical"
          preserve={false}
        >
          <Form.Item name="isListed" label="上架状态" valuePropName="checked">
            <Switch
              checkedChildren="已上架"
              unCheckedChildren="已下架"
              disabled={!listingTarget?.item.activeVersion}
            />
          </Form.Item>
          <Form.Item
            name="categoryId"
            label="分类"
            rules={[{ required: true, message: '请选择分类' }]}
          >
            <Select
              loading={listingCategoriesLoading}
              placeholder="从服务端分类中选择"
              options={listingCategories.map((category) => ({
                value: category.categoryId,
                label:
                  category.displayNameZh || categoryLabel(category.categoryId),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="categoryRank"
            label="分类内排序"
            extra={`数值越小越靠前；相同数值时按${itemLabel}标识稳定排序`}
            rules={[{ required: true, message: '请填写分类内排序' }]}
          >
            <InputNumber className="w-full" min={0} precision={0} />
          </Form.Item>
          <Form.Item name="featured" label="精选" valuePropName="checked">
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
          <Form.Item
            name="featuredRank"
            label="精选排序"
            extra="仅在开启精选时生效，数值越小越靠前"
            rules={
              featuredEnabled
                ? [{ required: true, message: '请填写精选排序' }]
                : undefined
            }
          >
            <InputNumber
              className="w-full"
              min={0}
              precision={0}
              disabled={!featuredEnabled}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        destroyOnHidden
        title={detail ? `${itemLabel} / ${detail.itemKey}` : ''}
        open={Boolean(detail)}
        width={960}
        onClose={closeDetail}
      >
        {detail ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {detailError ? (
              <Alert
                showIcon
                type="error"
                message="详情加载失败"
                description={detailError}
                action={
                  <Button size="small" onClick={() => void loadDetail(detail)}>
                    重试
                  </Button>
                }
              />
            ) : null}
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="统一版本">
                {detail.activeVersion || '未选择'}
              </Descriptions.Item>
              <Descriptions.Item label="乐观锁版本">
                {detail.lockVersion}
              </Descriptions.Item>
              <Descriptions.Item label="国内市场">
                <Space size={4} wrap>
                  <Tag
                    color={
                      listingOf(detail, 'domestic')?.isListed
                        ? 'green'
                        : undefined
                    }
                  >
                    {listingOf(detail, 'domestic')?.isListed
                      ? '已上架'
                      : '已下架'}
                  </Tag>
                  <Tag>
                    {categoryLabel(listingOf(detail, 'domestic')?.categoryId)}
                  </Tag>
                  {listingOf(detail, 'domestic')?.featured ? (
                    <Tag color="gold">精选</Tag>
                  ) : null}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="海外市场">
                <Space size={4} wrap>
                  <Tag
                    color={
                      listingOf(detail, 'overseas')?.isListed
                        ? 'green'
                        : undefined
                    }
                  >
                    {listingOf(detail, 'overseas')?.isListed
                      ? '已上架'
                      : '已下架'}
                  </Tag>
                  <Tag>
                    {categoryLabel(listingOf(detail, 'overseas')?.categoryId)}
                  </Tag>
                  {listingOf(detail, 'overseas')?.featured ? (
                    <Tag color="gold">精选</Tag>
                  ) : null}
                </Space>
              </Descriptions.Item>
            </Descriptions>
            <Tabs
              items={[
                {
                  key: 'releases',
                  label: `版本（${detailReleases.length}）`,
                  children: (
                    <Table
                      columns={releaseColumns}
                      dataSource={detailReleases}
                      loading={detailLoading}
                      pagination={false}
                      rowKey={(release) => String(release.releaseId)}
                    />
                  ),
                },
                {
                  key: 'manifest',
                  label: '当前 Manifest',
                  children: <MarketManifestOverview item={detail} />,
                },
                {
                  key: 'audit',
                  label: `审计日志（${auditLogs.length}）`,
                  children: (
                    <Table
                      columns={auditColumns}
                      dataSource={auditLogs}
                      loading={detailLoading}
                      pagination={false}
                      rowKey={(entry) => String(entry.auditId)}
                      scroll={{ x: 820 }}
                    />
                  ),
                },
              ]}
            />
          </Space>
        ) : null}
      </Drawer>
    </div>
  );
});

export function ConnectorManagementPage() {
  return <MarketManagementPage itemType="connector" />;
}
